/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-16 06:27:49
 *
 *******************************************************/
// @flow
import type { AnalyticsState, AnalyticsAction } from 'stores/flow-type/analytics.type';

import {
  GET_ANALYTICS_SETTINGS_REQUEST,
  GET_ANALYTICS_SETTINGS_SUCCESS,
  GET_ANALYTICS_SETTINGS_FAILURE,
  UPDATE_ANALYTICS_SETTINGS_REQUEST,
  UPDATE_ANALYTICS_SETTINGS_SUCCESS,
  UPDATE_ANALYTICS_SETTINGS_FAILURE,
  GET_TOTAL_REQUEST,
  GET_TOTAL_SUCCESS,
  GET_TOTAL_FAILURE,
  GET_TOTAL_ALL_SALES_REQUEST,
  GET_TOTAL_ALL_SALES_SUCCESS,
  GET_TOTAL_ALL_SALES_FAILURE,
  GET_TOP_FILTER_OPTION_VALUES_REQUEST,
  GET_TOP_FILTER_OPTION_VALUES_SUCCESS,
  GET_TOP_FILTER_OPTION_VALUES_FAILURE,
  GET_TOP_FILTER_OPTION_VALUE_COMBINATIONS_REQUEST,
  GET_TOP_FILTER_OPTION_VALUE_COMBINATIONS_SUCCESS,
  GET_TOP_FILTER_OPTION_VALUE_COMBINATIONS_FAILURE,
  GET_TOP_COLLECTIONS_TOTAL_FILTER_CLICKS_REQUEST,
  GET_TOP_COLLECTIONS_TOTAL_FILTER_CLICKS_SUCCESS,
  GET_TOP_COLLECTIONS_TOTAL_FILTER_CLICKS_FAILURE,
  GET_COLLECTION_TOTAL_DETAILS_BY_TIME_FILTER_CLICKS_REQUEST,
  GET_COLLECTION_TOTAL_DETAILS_BY_TIME_FILTER_CLICKS_SUCCESS,
  GET_COLLECTION_TOTAL_DETAILS_BY_TIME_FILTER_CLICKS_FAILURE,
  GET_TOP_SEARCH_TERMS_REQUEST,
  GET_TOP_SEARCH_TERMS_SUCCESS,
  GET_TOP_SEARCH_TERMS_FAILURE,
  GET_TOP_FILTER_OPTION_VALUES_ON_SEARCH_PAGE_REQUEST,
  GET_TOP_FILTER_OPTION_VALUES_ON_SEARCH_PAGE_SUCCESS,
  GET_TOP_FILTER_OPTION_VALUES_ON_SEARCH_PAGE_FAILURE,
  GET_TOTAL_SEARCHES_REQUEST,
  GET_TOTAL_SEARCHES_SUCCESS,
  GET_TOTAL_SEARCHES_FAILURE,
  GET_TOTAL_FILTER_CLICKS_ON_SEARCH_REQUEST,
  GET_TOTAL_FILTER_CLICKS_ON_SEARCH_SUCCESS,
  GET_TOTAL_FILTER_CLICKS_ON_SEARCH_FAILURE,
  GET_TOP_COLLECTIONS_TOTAL_SALES_REQUEST,
  GET_TOP_COLLECTIONS_TOTAL_SALES_SUCCESS,
  GET_TOP_COLLECTIONS_TOTAL_SALES_FAILURE,
  GET_TOTAL_SALES_REQUEST,
  GET_TOTAL_SALES_SUCCESS,
  GET_TOTAL_SALES_FAILURE,
  GET_TOTAL_SALES_ON_SEARCH_REQUEST,
  GET_TOTAL_SALES_ON_SEARCH_SUCCESS,
  GET_TOTAL_SALES_ON_SEARCH_FAILURE,
  EXPORT_DATA_REQUEST,
  EXPORT_DATA_SUCCESS,
  EXPORT_DATA_FAILURE,
  CHANGE_VALUE
} from 'stores/flow-type/analytics.type';

const initialState: AnalyticsState = {
  loading: false,
  exporting: false,
  success: false,
  error: undefined,
  totalFiltersClicks: 0,
  totalSearches: 0,
  totalSales: 0,
  totalQueryExecutionId: '',
  totalAllSalesQueryExecutionId: '',

  // Settings
  loadingAnalyticsSettings: false,
  savingAnalyticsSettings: false,
  savingFilterSetting: false,
  analyticsSettings: {},

  // Filter
  collection: { value: 'all', label: 'All collections' },
  fromDate: undefined,
  toDate: undefined,
  compare: false,

  // Collection
  // Top filter option values
  topFilterOptionValuesLoading: false,
  topFilterOptionValuesSuccess: false,
  topFilterOptionValues: [],
  topFilterOptionValuesQueryExecutionId: '',

  // Top filter option value combinations
  topFilterOptionValueCombinationsLoading: false,
  topFilterOptionValueCombinationsSuccess: false,
  topFilterOptionValueCombinations: [],
  topFilterOptionValueCombinationsQueryExecutionId: '',

  // Top collections total for filter clicks
  topCollectionsTotalFilterClicksLoading: false,
  topCollectionsTotalFilterClicksSuccess: false,
  topCollectionsTotalFilterClicks: [],
  topCollectionsTotalFilterClicksQueryExecutionId: '',

  // Collection total details by time for filter clicks
  collectionTotalDetailsByTimeFilterClicksLoading: false,
  collectionTotalDetailsByTimeFilterClicksSuccess: false,
  collectionTotalDetailsByTimeFilterClicks: [],
  collectionTotalDetailsByTimeFilterClicksCompared: [],
  collectionTotalDetailsByTimeFilterClicksTotal: 0,
  collectionTotalDetailsByTimeFilterClicksPercentChange: 0,
  collectionTotalDetailsByTimeFilterClicksQueryExecutionId: '',
  collectionTotalDetailsByTimeFilterClicksComparedQueryExecutionId: '',

  // Searchs
  // Top search term
  topSearchTermsLoading: false,
  topSearchTermsSuccess: false,
  topSearchTerms: [],
  topSearchTermsQueryExecutionId: '',

  // Top search term with no result
  topSearchTermsWithNoResultLoading: false,
  topSearchTermsWithNoResultSuccess: false,
  topSearchTermsWithNoResult: [],
  topSearchTermsWithNoResultQueryExecutionId: '',

  // Top filter option values on search page
  topFilterOptionValuesOnSearchPageLoading: false,
  topFilterOptionValuesOnSearchPageSuccess: false,
  topFilterOptionValuesOnSearchPage: [],
  topFilterOptionValuesOnSearchPageQueryExecutionId: '',

  // Total search
  totalSearchesLoading: false,
  totalSearchesSuccess: false,
  totalSearchesData: [],
  totalSearchesCompared: [],
  totalSearchesTotal: 0,
  totalSearchesPercentChange: 0,
  totalSearchesQueryExecutionId: '',
  totalSearchesComparedQueryExecutionId: '',

  // Total filter clicks on search
  totalFilterClicksOnSearchLoading: false,
  totalFilterClicksOnSearchSuccess: false,
  totalFilterClicksOnSearch: [],
  totalFilterClicksOnSearchCompared: [],
  totalFilterClicksOnSearchTotal: 0,
  totalFilterClicksOnSearchPercentChange: 0,
  totalFilterClicksOnSearchQueryExecutionId: '',
  totalFilterClicksOnSearchComparedExecutionId: '',

  // Top collections total sales
  topCollectionsTotalSalesLoading: false,
  topCollectionsTotalSalesSuccess: false,
  topCollectionsTotalSales: [],
  topCollectionsTotalSalesQueryExecutionId: '',

  // Collection total sales
  totalSalesLoading: false,
  totalSalesSuccess: false,
  totalSalesData: [],
  totalSalesCompared: [],
  totalSalesTotal: 0,
  totalSalesPercentChange: 0,
  totalSalesQueryExecutionId: '',

  // Total sales on search
  totalSalesOnSearchLoading: false,
  totalSalesOnSearchSuccess: false,
  totalSalesOnSearch: [],
  totalSalesOnSearchCompared: [],
  totalSalesOnSearchTotal: 0,
  totalSalesOnSearchPercentChange: 0,
  totalSalesOnSearchQueryExecutionId: ''
};

const AnalyticReducers = (state: AnalyticsState = initialState, action: AnalyticsAction) => {
  switch (action.type) {
    // Get settings
    case GET_ANALYTICS_SETTINGS_REQUEST:
      return { ...state, loadingAnalyticsSettings: true };
    case GET_ANALYTICS_SETTINGS_SUCCESS: {
      const { data } = action.payload.data;
      return {
        ...state,
        loadingAnalyticsSettings: false,
        error: undefined,
        analyticsSettings: { ...data }
      };
    }
    case GET_ANALYTICS_SETTINGS_FAILURE:
      return {
        ...state,
        loadingAnalyticsSettings: false,
        error: action.payload
      };

    //Update settings
    case UPDATE_ANALYTICS_SETTINGS_REQUEST:
      return { ...state, savingAnalyticsSettings: true };
    case UPDATE_ANALYTICS_SETTINGS_SUCCESS: {
      const { data } = action.payload.data;
      return {
        ...state,
        savingAnalyticsSettings: false,
        error: undefined,
        analyticsSettings: { ...data }
      };
    }
    case UPDATE_ANALYTICS_SETTINGS_FAILURE:
      return {
        ...state,
        savingAnalyticsSettings: false,
        error: action.payload
      };

    // Get total
    case GET_TOTAL_REQUEST:
      return { ...state, success: false, loading: true };
    case GET_TOTAL_SUCCESS: {
      const { data } = action.payload;
      let { totalFilters, totalSearches, queryExecutionId } = data;

      return {
        ...state,
        loading: false,
        success: true,
        totalFilters,
        totalSearches,
        totalQueryExecutionId: queryExecutionId
      };
    }
    case GET_TOTAL_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };

    // Get total all sales
    case GET_TOTAL_ALL_SALES_REQUEST:
      return { ...state, success: false, loading: true };
    case GET_TOTAL_ALL_SALES_SUCCESS: {
      const { data } = action.payload;
      let { totalSales, queryExecutionId } = data;

      return {
        ...state,
        loading: false,
        success: true,
        totalSales,
        totalAllSalesQueryExecutionId: queryExecutionId
      };
    }
    case GET_TOTAL_ALL_SALES_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };

    // Top filter option values
    case GET_TOP_FILTER_OPTION_VALUES_REQUEST:
      return { ...state, topFilterOptionValuesSuccess: false, topFilterOptionValuesLoading: true };
    case GET_TOP_FILTER_OPTION_VALUES_SUCCESS: {
      let { data } = action.payload;

      const { data: topFilterOptionValues, queryExecutionId } = data;

      return {
        ...state,
        topFilterOptionValuesLoading: false,
        topFilterOptionValuesSuccess: true,
        topFilterOptionValues,
        topFilterOptionValuesQueryExecutionId: queryExecutionId
      };
    }
    case GET_TOP_FILTER_OPTION_VALUES_FAILURE: {
      return {
        ...state,
        topFilterOptionValuesLoading: false,
        topFilterOptionValuesSuccess: false,
        error: action.payload
      };
    }

    // Top filter option value combinations
    case GET_TOP_FILTER_OPTION_VALUE_COMBINATIONS_REQUEST:
      return {
        ...state,
        topFilterOptionValueCombinationsSuccess: false,
        topFilterOptionValueCombinationsLoading: true
      };
    case GET_TOP_FILTER_OPTION_VALUE_COMBINATIONS_SUCCESS: {
      let { data } = action.payload;

      const { data: topFilterOptionValueCombinations, queryExecutionId } = data;

      return {
        ...state,
        topFilterOptionValueCombinationsLoading: false,
        topFilterOptionValueCombinationsSuccess: true,
        topFilterOptionValueCombinations,
        topFilterOptionValueCombinationsQueryExecutionId: queryExecutionId
      };
    }
    case GET_TOP_FILTER_OPTION_VALUE_COMBINATIONS_FAILURE: {
      return {
        ...state,
        topFilterOptionValueCombinationsLoading: false,
        topFilterOptionValueCombinationsSuccess: false,
        error: action.payload
      };
    }

    case CHANGE_VALUE:
      const { name, value } = action.payload;
      return {
        ...state,
        [name]: value
      };

    // Get top collections total for filter clicks
    case GET_TOP_COLLECTIONS_TOTAL_FILTER_CLICKS_REQUEST:
      return {
        ...state,
        topCollectionsTotalFilterClicksSuccess: false,
        topCollectionsTotalFilterClicksLoading: true
      };
    case GET_TOP_COLLECTIONS_TOTAL_FILTER_CLICKS_SUCCESS: {
      let { data: topCollectionsTotalFilterClicks, queryExecutionId } = action.payload.data;

      return {
        ...state,
        topCollectionsTotalFilterClicksLoading: false,
        topCollectionsTotalFilterClicksSuccess: true,
        topCollectionsTotalFilterClicks,
        topCollectionsTotalFilterClicksQueryExecutionId: queryExecutionId
      };
    }
    case GET_TOP_COLLECTIONS_TOTAL_FILTER_CLICKS_FAILURE:
      return {
        ...state,
        topCollectionsTotalFilterClicksLoading: false,
        topCollectionsTotalFilterClicksSuccess: false,
        error: action.payload
      };

    // Get collection total details by time for filter clicks
    case GET_COLLECTION_TOTAL_DETAILS_BY_TIME_FILTER_CLICKS_REQUEST:
      return {
        ...state,
        collectionTotalDetailsByTimeFilterClicksSuccess: false,
        collectionTotalDetailsByTimeFilterClicksLoading: true
      };
    case GET_COLLECTION_TOTAL_DETAILS_BY_TIME_FILTER_CLICKS_SUCCESS: {
      let {
        data = [],
        comparedData = [],
        total,
        percentChange,
        queryExecutionId,
        comparedExecutionId
      } = action.payload.data;

      return {
        ...state,
        collectionTotalDetailsByTimeFilterClicksLoading: false,
        collectionTotalDetailsByTimeFilterClicksSuccess: true,
        collectionTotalDetailsByTimeFilterClicks: data,
        collectionTotalDetailsByTimeFilterClicksCompared: comparedData,
        collectionTotalDetailsByTimeFilterClicksTotal: total,
        collectionTotalDetailsByTimeFilterClicksPercentChange: percentChange,
        collectionTotalDetailsByTimeFilterClicksQueryExecutionId: queryExecutionId,
        collectionTotalDetailsByTimeFilterClicksComparedQueryExecutionId: comparedExecutionId
      };
    }
    case GET_COLLECTION_TOTAL_DETAILS_BY_TIME_FILTER_CLICKS_FAILURE:
      return {
        ...state,
        collectionTotalDetailsByTimeFilterClicksLoading: false,
        collectionTotalDetailsByTimeFilterClicksSuccess: false,
        error: action.payload
      };

    // Top filter option values
    case GET_TOP_FILTER_OPTION_VALUES_ON_SEARCH_PAGE_REQUEST:
      return {
        ...state,
        topFilterOptionValuesOnSearchPageSuccess: false,
        topFilterOptionValuesOnSearchPageLoading: true
      };
    case GET_TOP_FILTER_OPTION_VALUES_ON_SEARCH_PAGE_SUCCESS: {
      const { data } = action.payload;

      const { data: topFilterOptionValuesOnSearchPage, queryExecutionId } = data;

      return {
        ...state,
        topFilterOptionValuesOnSearchPageLoading: false,
        topFilterOptionValuesOnSearchPageSuccess: true,
        topFilterOptionValuesOnSearchPage,
        topFilterOptionValuesOnSearchPageQueryExecutionId: queryExecutionId
      };
    }
    case GET_TOP_FILTER_OPTION_VALUES_ON_SEARCH_PAGE_FAILURE: {
      return {
        ...state,
        topFilterOptionValuesOnSearchPageLoading: false,
        topFilterOptionValuesOnSearchPageSuccess: false,
        error: action.payload
      };
    }

    // Top filter option values
    case GET_TOP_SEARCH_TERMS_REQUEST: {
      const { noResult } = action.payload;
      return noResult
        ? {
            ...state,
            topSearchTermsWithNoResultSuccess: false,
            topSearchTermsWithNoResultLoading: true
          }
        : { ...state, topSearchTermsSuccess: false, topSearchTermsLoading: true };
    }
    case GET_TOP_SEARCH_TERMS_SUCCESS: {
      let { data, noResult } = action.payload;

      const { data: topSearchTerms, queryExecutionId } = data;

      return noResult
        ? {
            ...state,
            topSearchTermsWithNoResultLoading: false,
            topSearchTermsWithNoResultSuccess: true,
            topSearchTermsWithNoResult: topSearchTerms,
            topSearchTermsWithNoResultQueryExecutionId: queryExecutionId
          }
        : {
            ...state,
            topSearchTermsLoading: false,
            topSearchTermsSuccess: true,
            topSearchTerms,
            topSearchTermsQueryExecutionId: queryExecutionId
          };
    }
    case GET_TOP_SEARCH_TERMS_FAILURE: {
      let { noResult } = action.payload;

      return noResult
        ? {
            ...state,
            topSearchTermsWithNoResultLoading: false,
            topSearchTermsWithNoResultSuccess: false,
            error: action.payload
          }
        : {
            ...state,
            topSearchTermsLoading: false,
            topSearchTermsSuccess: false,
            error: action.payload
          };
    }

    // Top filter option values
    case GET_TOTAL_SEARCHES_REQUEST:
      return {
        ...state,
        totalSearchesSuccess: false,
        totalSearchesLoading: true
      };
    case GET_TOTAL_SEARCHES_SUCCESS: {
      const { data } = action.payload;

      const {
        total: totalSearchesTotal,
        data: totalSearchesData,
        comparedData: totalSearchesCompared,
        percentChange: totalSearchesPercentChange,
        queryExecutionId,
        comparedExecutionId
      } = data;

      return {
        ...state,
        totalSearchesLoading: false,
        totalSearchesSuccess: true,
        totalSearchesData,
        totalSearchesCompared,
        totalSearchesTotal,
        totalSearchesPercentChange,
        totalSearchesQueryExecutionId: queryExecutionId,
        totalSearchesComparedQueryExecutionId: comparedExecutionId
      };
    }
    case GET_TOTAL_SEARCHES_FAILURE: {
      return {
        ...state,
        totalSearchesLoading: false,
        totalSearchesSuccess: false,
        error: action.payload
      };
    }

    // Top filter click on search
    case GET_TOTAL_FILTER_CLICKS_ON_SEARCH_REQUEST:
      return {
        ...state,
        totalFilterClicksOnSearchSuccess: false,
        totalFilterClicksOnSearchLoading: true
      };
    case GET_TOTAL_FILTER_CLICKS_ON_SEARCH_SUCCESS: {
      const { data } = action.payload;

      const {
        total: totalFilterClicksOnSearchTotal,
        data: totalFilterClicksOnSearch,
        comparedData: totalFilterClicksOnSearchCompared,
        percentChange: totalFilterClicksOnSearchPercentChange,
        queryExecutionId,
        comparedExecutionId
      } = data;

      return {
        ...state,
        totalFilterClicksOnSearchLoading: false,
        totalFilterClicksOnSearchSuccess: true,
        totalFilterClicksOnSearch,
        totalFilterClicksOnSearchCompared,
        totalFilterClicksOnSearchTotal,
        totalFilterClicksOnSearchPercentChange,
        totalFilterClicksOnSearchQueryExecutionId: queryExecutionId,
        totalFilterClicksOnSearchComparedExecutionId: comparedExecutionId
      };
    }
    case GET_TOTAL_FILTER_CLICKS_ON_SEARCH_FAILURE: {
      return {
        ...state,
        totalFilterClicksOnSearchLoading: false,
        totalFilterClicksOnSearchSuccess: false,
        error: action.payload
      };
    }

    // Get top collections total for sales
    case GET_TOP_COLLECTIONS_TOTAL_SALES_REQUEST:
      return {
        ...state,
        topCollectionsTotalSalesSuccess: false,
        topCollectionsTotalSalesLoading: true
      };
    case GET_TOP_COLLECTIONS_TOTAL_SALES_SUCCESS: {
      let { data: topCollectionsTotalSales, queryExecutionId } = action.payload.data;
      return {
        ...state,
        topCollectionsTotalSalesLoading: false,
        topCollectionsTotalSalesSuccess: true,
        topCollectionsTotalSales,
        topCollectionsTotalSalesQueryExecutionId: queryExecutionId
      };
    }
    case GET_TOP_COLLECTIONS_TOTAL_SALES_FAILURE:
      return {
        ...state,
        topCollectionsTotalSalesLoading: false,
        topCollectionsTotalSalesSuccess: false,
        error: action.payload
      };

    // Get collection total sales by time
    case GET_TOTAL_SALES_REQUEST:
      return {
        ...state,
        totalSalesSuccess: false,
        totalSalesLoading: true
      };
    case GET_TOTAL_SALES_SUCCESS: {
      let {
        data = [],
        comparedData = [],
        total,
        percentChange,
        queryExecutionId,
        comparedExecutionId
      } = action.payload.data;

      return {
        ...state,
        totalSalesLoading: false,
        totalSalesSuccess: true,
        totalSalesData: data,
        totalSalesCompared: comparedData,
        totalSalesTotal: total,
        totalSalesPercentChange: percentChange,
        totalSalesQueryExecutionId: queryExecutionId,
        totalSalesComparedQueryExecutionId: comparedExecutionId
      };
    }
    case GET_TOTAL_SALES_FAILURE:
      return {
        ...state,
        totalSalesLoading: false,
        totalSalesSuccess: false,
        error: action.payload
      };

    // Get total sales on search
    case GET_TOTAL_SALES_ON_SEARCH_REQUEST:
      return {
        ...state,
        totalSalesOnSearchSuccess: false,
        totalSalesOnSearchLoading: true
      };
    case GET_TOTAL_SALES_ON_SEARCH_SUCCESS: {
      let {
        data = [],
        comparedData = [],
        total,
        percentChange,
        queryExecutionId,
        comparedExecutionId
      } = action.payload.data;

      return {
        ...state,
        totalSalesOnSearchLoading: false,
        totalSalesOnSearchSuccess: true,
        totalSalesOnSearch: data,
        totalSalesOnSearchCompared: comparedData,
        totalSalesOnSearchTotal: total,
        totalSalesOnSearchPercentChange: percentChange,
        totalSalesOnSearchQueryExecutionId: queryExecutionId,
        totalSalesOnSearchComparedQueryExecutionId: comparedExecutionId
      };
    }
    case GET_TOTAL_SALES_ON_SEARCH_FAILURE:
      return {
        ...state,
        totalSalesOnSearchLoading: false,
        totalSalesOnSearchSuccess: false,
        error: action.payload
      };
    case EXPORT_DATA_REQUEST:
      return {
        ...state,
        exporting: true
      };
    case EXPORT_DATA_SUCCESS: {
      return {
        ...state,
        exporting: false
      };
    }
    case EXPORT_DATA_FAILURE:
      return {
        ...state,
        exporting: false,
        error: action.payload
      };
    default:
      return state;
  }
};
export default AnalyticReducers;
